import type { FieldSection } from './field-view';
import type { FieldActorId } from '@mio/shared';

export const FIELD_TOUR_CHAPTERS = [
  { id: 'brief', label: 'Brief' },
  { id: 'people', label: 'People' },
  { id: 'understanding', label: 'Understanding' },
  { id: 'build', label: 'Build' },
  { id: 'submit', label: 'Submit' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'tools', label: 'Tools' },
] as const;

export type FieldTourChapterId = (typeof FIELD_TOUR_CHAPTERS)[number]['id'];

export interface FieldTourStep {
  readonly id: string;
  readonly chapter: FieldTourChapterId;
  readonly shortLabel: string;
  readonly title: string;
  readonly description: string;
  readonly details: readonly string[];
  readonly tip: string;
  readonly target: string;
  readonly fallbackTarget?: string;
  readonly destination: {
    readonly section: FieldSection;
    readonly actorId?: FieldActorId;
    readonly tab?: 'brief' | 'decisions' | 'findings';
    readonly form?: 'new-submission' | 'decision';
  };
}

/** Tour destinations reveal the existing interface; completing a step never performs work. */
export const FIELD_TOUR_STEPS: readonly FieldTourStep[] = [
  {
    id: 'working-brief',
    chapter: 'brief',
    shortLabel: 'Working brief',
    title: 'Start with the problem, not a finished specification',
    description:
      'This is your product starting point. The brief explains what the company wants to achieve, while leaving details for you to discover with its colleagues.',
    details: [
      'Read the product context before choosing an implementation.',
      'Notice the brief version so later conversations can refer to the same starting point.',
      'Treat missing detail as a question to investigate, rather than an instruction to guess silently.',
    ],
    tip: 'You can begin a small prototype before every question has an answer.',
    target: 'brief-overview',
    destination: { section: 'engagement', tab: 'brief' },
  },
  {
    id: 'success-measures',
    chapter: 'brief',
    shortLabel: 'Success',
    title: 'Describe the result someone should experience',
    description:
      'Use the success measures to connect your code to an observable product outcome. A working endpoint alone may not explain whether the intended user can complete the journey.',
    details: [
      'Identify who uses the product and what they should accomplish.',
      'Ask Jordan what the user should see; ask Marcus how the business judges success.',
      'Turn broad goals into examples you can show and verify.',
    ],
    tip: 'Keep the difference between a technical check and a useful product outcome visible.',
    target: 'brief-success',
    fallbackTarget: 'brief-overview',
    destination: { section: 'engagement', tab: 'brief' },
  },
  {
    id: 'known-constraints',
    chapter: 'brief',
    shortLabel: 'Constraints',
    title: 'Separate stated boundaries from open choices',
    description:
      'Known constraints tell you what your increment must respect. They do not fill in every permission rule, screen detail or recovery decision.',
    details: [
      'Preserve explicit constraints when you sketch a solution.',
      'Record an assumption when you need a temporary implementation choice.',
      'Ask the relevant owner before treating an unresolved choice as agreed behavior.',
    ],
    tip: 'A newly clarified expectation should be distinguishable from the original agreed scope.',
    target: 'brief-constraints',
    fallbackTarget: 'brief-overview',
    destination: { section: 'engagement', tab: 'brief' },
  },
  {
    id: 'delivery-outcomes',
    chapter: 'brief',
    shortLabel: 'Outcomes',
    title: 'Choose an outcome for your first increment',
    description:
      'Delivery outcomes give you a way to organize discovery and implementation. Expand an outcome when you want more context, then choose a manageable slice of the work.',
    details: [
      'Connect your proposed change to an outcome in the brief.',
      'Look for a journey you can demonstrate from beginning to end.',
      'Use the starting points below as guidance, not a prerequisite checklist.',
    ],
    tip: 'An early submission can expose the next useful question.',
    target: 'brief-outcomes',
    fallbackTarget: 'brief-overview',
    destination: { section: 'engagement', tab: 'brief' },
  },
  {
    id: 'choose-colleague',
    chapter: 'people',
    shortLabel: 'Colleagues',
    title: 'Choose the owner of the question in front of you',
    description:
      'Each colleague has a different responsibility. Their role and focus help you decide whose answer would remove uncertainty from your next change.',
    details: [
      'Use the colleague list, or Change colleague on a narrow screen, to switch conversations.',
      'Each direct conversation keeps its own draft and attached references.',
      'You do not have to interview all five colleagues before you start building.',
    ],
    tip: 'Start with the colleague whose answer would change your next implementation decision.',
    target: 'colleague-directory',
    fallbackTarget: 'colleague-identity',
    destination: { section: 'inbox', actorId: 'jordan' },
  },
  {
    id: 'ask-jordan',
    chapter: 'people',
    shortLabel: 'Jordan',
    title: 'Jordan: clarify the product journey',
    description:
      'Jordan is the person to ask about product behavior and the experience you are trying to deliver. Use a concrete example rather than asking whether the whole project looks right.',
    details: [
      'Ask which user journey matters first and what each step should show.',
      'Clarify expected empty states, errors and visible confirmation.',
      'Share a submission when you want product feedback on a real implementation.',
    ],
    tip: 'Example: What should the advisor see after a proposal is declined?',
    target: 'colleague-identity',
    destination: { section: 'inbox', actorId: 'jordan' },
  },
  {
    id: 'ask-noor',
    chapter: 'people',
    shortLabel: 'Noor',
    title: 'Noor: understand the engineering boundaries',
    description:
      'Noor helps clarify code, architecture, integration contracts and verification. Bring the specific boundary or tradeoff you need to understand.',
    details: [
      'Ask which system owns a record and which interfaces your change must preserve.',
      'Discuss failure handling, consistency and a suitable test for your proposed behavior.',
      'Use source and run evidence when following up on a code-review finding.',
    ],
    tip: 'Example: Which existing order contract should this increment call?',
    target: 'colleague-identity',
    destination: { section: 'inbox', actorId: 'noor' },
  },
  {
    id: 'ask-elena',
    chapter: 'people',
    shortLabel: 'Elena',
    title: 'Elena: establish permissions and approval rules',
    description:
      'Elena clarifies authority, approvals and security expectations. Do not assume a particular role matrix or approval lifetime merely because it seems plausible.',
    details: [
      'Ask who may propose, approve and execute an action.',
      'Clarify what an approval applies to and when it must be checked again.',
      'Record unresolved policy choices separately from confirmed requirements.',
    ],
    tip: 'Example: What changes to a proposal require a fresh approval?',
    target: 'colleague-identity',
    destination: { section: 'inbox', actorId: 'elena' },
  },
  {
    id: 'ask-marcus',
    chapter: 'people',
    shortLabel: 'Marcus',
    title: 'Marcus: connect the increment to business scope',
    description:
      'Marcus helps establish who the pilot serves and what would make it worthwhile. Separate a business objective from an implementation detail.',
    details: [
      'Ask which users and situations belong in the first pilot.',
      'Clarify ambiguous measures such as margin, success or expected volume.',
      'Discuss which proposed improvements are needed now and which can wait.',
    ],
    tip: 'Example: Which outcome would make this first pilot successful?',
    target: 'colleague-identity',
    destination: { section: 'inbox', actorId: 'marcus' },
  },
  {
    id: 'ask-amara',
    chapter: 'people',
    shortLabel: 'Amara',
    title: 'Amara: plan for failure, recovery and handoff',
    description:
      'Amara focuses on how the system will be operated. A successful first request is only part of a journey that someone must support when things go wrong.',
    details: [
      'Ask how an operator should identify a failed or uncertain action.',
      'Clarify safe retry, recovery and rollback expectations.',
      'Bring the current candidate and its evidence when you are ready to discuss release.',
    ],
    tip: 'Example: How should an operator recover an order with an uncertain result?',
    target: 'colleague-identity',
    destination: { section: 'inbox', actorId: 'amara' },
  },
  {
    id: 'write-question',
    chapter: 'people',
    shortLabel: 'Ask clearly',
    title: 'Give the colleague enough context to answer',
    description:
      'Describe what you understand, what remains uncertain and the decision you need. Your draft stays yours until you choose to send it.',
    details: [
      'State the behavior you are considering and why the answer matters.',
      'Ask one focused question or a small related set of questions.',
      'Enter sends a typed message; Shift + Enter adds a new line.',
    ],
    tip: 'Explain your current interpretation first so the colleague can correct a specific misunderstanding.',
    target: 'message-composer',
    destination: { section: 'inbox', actorId: 'jordan' },
  },
  {
    id: 'attach-context',
    chapter: 'people',
    shortLabel: 'References',
    title: 'Attach the work your question is about',
    description:
      'References connect a conversation to a brief, understanding record, finding, submission, run or document. This helps the colleague inspect the intended context.',
    details: [
      'Use Attach a reference to choose an item available in your engagement.',
      'Check the attached labels and remove references that do not belong to this question.',
      'References selected in one colleague conversation do not become another conversation’s attachments.',
    ],
    tip: 'A label in a reply can open the exact referenced report or finding when it is available.',
    target: 'message-references',
    fallbackTarget: 'message-composer',
    destination: { section: 'inbox', actorId: 'jordan' },
  },
  {
    id: 'voice-conversation',
    chapter: 'people',
    shortLabel: 'Voice',
    title: 'Use a call when speaking is more convenient',
    description:
      'When voice is enabled, Call starts a conversation with the selected colleague. The disabled control explains when voice is unavailable for this engagement.',
    details: [
      'You choose when to start the call and grant microphone access.',
      'Committed speech is sent as conversation turns; mute or end the call when needed.',
      'Review the conversation afterward and record any agreement that affects your implementation.',
    ],
    tip: 'Check the selected colleague and relevant references before starting.',
    target: 'voice-call',
    fallbackTarget: 'message-composer',
    destination: { section: 'inbox', actorId: 'jordan' },
  },
  {
    id: 'understanding-records',
    chapter: 'understanding',
    shortLabel: 'Understanding',
    title: 'Make the current understanding visible',
    description:
      'This area separates open questions, assumptions, requirements and scope decisions. An empty list is normal before you have recorded your first discovery.',
    details: [
      'Create a record when a conversation changes how you understand the work.',
      'Keep uncertainty visible instead of presenting it as an agreed requirement.',
      'Link the record to supporting evidence and the delivery outcome it affects.',
    ],
    tip: 'A record organizes the discussion; creating it alone does not confirm it.',
    target: 'understanding',
    destination: { section: 'engagement', tab: 'decisions' },
  },
  {
    id: 'record-type-owner',
    chapter: 'understanding',
    shortLabel: 'Type and owner',
    title: 'Choose what you are recording and who can confirm it',
    description:
      'Use the record type to distinguish a question from an assumption, requirement or scope decision. Assign the colleague who has authority to clarify this subject.',
    details: [
      'Give the record a title that describes the uncertainty or behavior.',
      'Choose the colleague who can confirm it, rather than treating your own draft as approval.',
      'Describe the impact if the assumption is wrong or the question remains unanswered.',
    ],
    tip: 'Use a question for an unknown rule and an assumption for a temporary choice you need confirmed.',
    target: 'decision-type-owner',
    fallbackTarget: 'understanding',
    destination: { section: 'engagement', tab: 'decisions', form: 'decision' },
  },
  {
    id: 'acceptance-examples',
    chapter: 'understanding',
    shortLabel: 'Acceptance',
    title: 'Write examples someone can actually verify',
    description:
      'Acceptance examples turn an abstract requirement into observable behavior. Include the important success and failure cases for this increment.',
    details: [
      'Describe the starting situation, action and expected result.',
      'Put each acceptance example on its own line in the form.',
      'Attach relevant evidence and choose the outcomes this record supports.',
    ],
    tip: 'Prefer “the user sees a pending result” to an untestable phrase such as “works well.”',
    target: 'decision-acceptance',
    fallbackTarget: 'understanding',
    destination: { section: 'engagement', tab: 'decisions', form: 'decision' },
  },
  {
    id: 'owner-confirmation',
    chapter: 'understanding',
    shortLabel: 'Confirmation',
    title: 'Ask the owner to confirm a saved record',
    description:
      'Saved records show their status, owner and version. Request confirmation when you want the owner to review what you wrote; their response also appears in Conversations.',
    details: [
      'Before any records exist, create and save one when you are ready.',
      'Read the recorded owner response instead of assuming the request was accepted.',
      'Revisit supporting evidence and version history when the agreed behavior changes.',
    ],
    tip: 'If the owner disagrees, revise the record and keep the earlier version as context.',
    target: 'decision-records',
    fallbackTarget: 'understanding',
    destination: { section: 'engagement', tab: 'decisions' },
  },
  {
    id: 'build-route',
    chapter: 'build',
    shortLabel: 'Build',
    title: 'Build a small, useful increment',
    description:
      'Use the Workbench editor or work in your own tools and upload the project later. Start with a slice you can demonstrate while independent questions remain open.',
    details: [
      'Keep the brief and agreed examples beside your implementation choices.',
      'Preserve the starter’s relevant interfaces and project structure.',
      'Ask Noor about an unclear engineering contract before replacing it.',
    ],
    tip: 'Choose one success journey and one meaningful failure case you can demonstrate.',
    target: 'build-intro',
    destination: { section: 'build' },
  },
  {
    id: 'starter-files',
    chapter: 'build',
    shortLabel: 'Starter',
    title: 'Use the starter as the shared starting point',
    description:
      'Starter files gives you the project to inspect in your own tools. Read its configuration and contracts alongside the brief before choosing your first change.',
    details: [
      'Inspect the entry points and supplied interfaces before changing behavior.',
      'Keep source files and supporting configuration together.',
      'Download and save actions do not create a review submission.',
    ],
    tip: 'A small change is easier to review when its surrounding contracts remain recognizable.',
    target: 'starter-download',
    fallbackTarget: 'build-intro',
    destination: { section: 'build' },
  },
  {
    id: 'editor-checkpoints',
    chapter: 'build',
    shortLabel: 'Editor and saves',
    title: 'Open the editor when ready',
    description:
      'Opening the editor area is separate from starting the Workbench workspace. Its controls explain whether the environment is ready, unavailable or still being used by Workshop.',
    details: [
      'Start Workbench explicitly; if Workshop is active, use the offered save-and-switch flow.',
      'Save creates a checkpoint, while Save & stop also stops the workspace.',
      'Keep the editor connected during saving and submission so open buffers can be captured.',
    ],
    tip: 'A saved checkpoint is not a submitted version; submit the increment when you want colleague feedback.',
    target: 'build-editor',
    fallbackTarget: 'build-intro',
    destination: { section: 'build' },
  },
  {
    id: 'submission-title',
    chapter: 'submit',
    shortLabel: 'Title',
    title: 'Name the increment you want reviewed',
    description:
      'New submission opens a draft for an exact version of your work. Choose a title that helps a reviewer recognize what this increment is intended to achieve.',
    details: [
      'Use a specific title such as the journey or behavior you changed.',
      'An early prototype is a valid reason to ask for feedback.',
      'Opening this form does not save a PR or start any reviews.',
    ],
    tip: 'A title such as “Show pending approval in the proposal journey” makes the intended change clear.',
    target: 'submission-title',
    destination: { section: 'reviews', form: 'new-submission' },
  },
  {
    id: 'submission-context',
    chapter: 'submit',
    shortLabel: 'Review context',
    title: 'Explain what changed and what you checked',
    description:
      'The description gives your reviewers the purpose of the increment. Be clear about what works, what is incomplete and what kind of feedback would help.',
    details: [
      'Describe the change and the product requirement it is meant to address.',
      'State which checks you actually performed and their results.',
      'Name remaining questions or limitations without claiming unperformed verification.',
    ],
    tip: 'The reviewers also receive relevant project context; this description makes your intent explicit.',
    target: 'submission-context',
    fallbackTarget: 'submission-title',
    destination: { section: 'reviews', form: 'new-submission' },
  },
  {
    id: 'submission-ticket',
    chapter: 'submit',
    shortLabel: 'Linked work',
    title: 'Connect the submission to planned work when useful',
    description:
      'A linked ticket connects the source version to an increment you already planned. It is optional, so an empty ticket list does not stop you submitting a prototype.',
    details: [
      'Choose the ticket that describes this change, if one exists.',
      'Check its acceptance criteria before describing the increment as complete.',
      'Leave No ticket selected when the submission is exploratory.',
    ],
    tip: 'Use the ticket to keep the change focused; describe any additional scope in the submission.',
    target: 'submission-ticket',
    fallbackTarget: 'submission-title',
    destination: { section: 'reviews', form: 'new-submission' },
  },
  {
    id: 'submission-reviewers',
    chapter: 'submit',
    shortLabel: 'Reviewers',
    title: 'Choose the owners whose feedback you need',
    description:
      'Noor and Jordan are selected by default for engineering and product review. You can include other colleagues when the increment also needs their perspective.',
    details: [
      'Elena reviews permissions and approvals; Marcus reviews business and pilot scope.',
      'Amara reviews recovery, operations and handoff.',
      'Keep at least one reviewer selected before submitting.',
    ],
    tip: 'Choose perspectives that match the change; a permission change may need Elena as well as Noor.',
    target: 'submission-reviewers',
    fallbackTarget: 'submission-title',
    destination: { section: 'reviews', form: 'new-submission' },
  },
  {
    id: 'submission-ide',
    chapter: 'submit',
    shortLabel: 'IDE capture',
    title: 'Submit a frozen copy from the connected editor',
    description:
      'Submit from Workbench IDE captures the connected workspace and sends that version for review. The control explains what is missing when capture is not ready.',
    details: [
      'Supply a title and reviewers, then start and connect the Workbench IDE yourself.',
      'Keep the editor connected until the submission finishes.',
      'If native capture is unavailable, use the source upload route below.',
    ],
    tip: 'This is a submission action, not just Save; verify that the connected workspace contains your intended changes.',
    target: 'submission-capture',
    fallbackTarget: 'submission-sources',
    destination: { section: 'reviews', form: 'new-submission' },
  },
  {
    id: 'submission-upload',
    chapter: 'submit',
    shortLabel: 'Source upload',
    title: 'Upload the project structure your reviewers need',
    description:
      'Choose project folder preserves directories; individual file selection uses filenames only. Select a complete source snapshot, including the configuration needed to understand it.',
    details: [
      'The picker accepts UTF-8 source within the displayed file and size limits.',
      'After selection, inspect included paths and any listed private or generated exclusions.',
      'Remove secrets from your project; automatic exclusions are not a substitute for checking your files.',
    ],
    tip: 'Prefer a project folder when imports or configuration depend on relative paths.',
    target: 'submission-sources',
    fallbackTarget: 'submission-title',
    destination: { section: 'reviews', form: 'new-submission' },
  },
  {
    id: 'submission-send',
    chapter: 'submit',
    shortLabel: 'Submit version',
    title: 'Submit when you are ready for real feedback',
    description:
      'Submit uploaded source saves the selected version and starts checks and colleague reviews. The button stays unavailable until the required title, source and reviewer selection are present.',
    details: [
      'Review your draft and source selection before choosing Submit.',
      'Later local edits do not change the version that was already submitted.',
      'You can continue working while the review runs; feedback also arrives in Conversations.',
    ],
    tip: 'Keep the recorded source revision when discussing feedback so everyone refers to the same code.',
    target: 'submission-submit',
    fallbackTarget: 'submission-title',
    destination: { section: 'reviews', form: 'new-submission' },
  },
  {
    id: 'revision-history',
    chapter: 'feedback',
    shortLabel: 'Review progress',
    title: 'Follow the submitted version through checks and review',
    description:
      'Saved submissions separate source capture, verification and colleague review. This area is empty before your first submission, then records the versions you send for feedback.',
    details: [
      'Match the source revision before comparing a run or review to your code.',
      'Detailed reviews may take several minutes; progress shows what is happening.',
      'A completed review sends an automatic colleague message without needing a chat prompt.',
    ],
    tip: 'Passing checks and receiving a review are separate from product acceptance.',
    target: 'review-history',
    destination: { section: 'reviews' },
  },
  {
    id: 'review-evidence',
    chapter: 'feedback',
    shortLabel: 'Report details',
    title: 'Read the evidence behind the reviewer’s conclusion',
    description:
      'When a report exists, it explains what works, how requirements were assessed and what needs attention. Until then, the review area shows the appropriate empty or pending state.',
    details: [
      'Use expected behavior, actual behavior, impact and recommended verification to plan a fix.',
      'Distinguish source inspection from a test or browser observation that actually ran.',
      'Check limitations and previous-finding comparisons; a historical report belongs to its recorded version.',
    ],
    tip: 'Open the report from its proactive message whenever you need the full explanation.',
    target: 'review-report',
    fallbackTarget: 'review-history',
    destination: { section: 'reviews' },
  },
  {
    id: 'automatic-review-message',
    chapter: 'feedback',
    shortLabel: 'Automatic feedback',
    title: 'Your reviewer messages you without being prompted',
    description:
      'A completed review sends a colleague message linked to the reviewed work. Before it finishes, no review message may appear; you do not need to ask for one.',
    details: [
      'Open the available report or finding references to inspect the full feedback.',
      'Ask a focused follow-up and attach the relevant work when a conclusion needs clarification.',
      'Submit changed source as an updated version; saying you fixed it in chat does not update the reviewed code.',
    ],
    tip: 'Each requested reviewer sends their own response when their review completes.',
    target: 'review-messages',
    fallbackTarget: 'message-composer',
    destination: { section: 'inbox', actorId: 'noor' },
  },
  {
    id: 'finding-categories',
    chapter: 'feedback',
    shortLabel: 'Findings',
    title: 'Understand what kind of follow-up is being requested',
    description:
      'Findings separate implementation defects from clarifications, discovery gaps and scope changes. If none exist yet, this area explains how findings follow the evidence.',
    details: [
      'Read the category, owner and supporting evidence before deciding on an action.',
      'A missing product decision may need clarification rather than an immediate code change.',
      'Use the status filter to revisit open, deferred or resolved work.',
    ],
    tip: 'New expectations should remain distinguishable from defects against agreed requirements.',
    target: 'findings',
    destination: { section: 'engagement', tab: 'findings' },
  },
  {
    id: 'finding-response',
    chapter: 'feedback',
    shortLabel: 'Respond and revise',
    title: 'Give the owner evidence for the next decision',
    description:
      'A finding can lead to discussion, a ticket or a formal response. Controls depend on its current status; before findings exist, use this guide to understand the available follow-up.',
    details: [
      'Dispute a conclusion with evidence, propose deferral, or request resolution review after a fix.',
      'Submit changed code as an updated version of the relevant PR and attach matching evidence.',
      'Read your saved response and the recorded owner decision; requesting closure does not close the finding.',
    ],
    tip: 'Deferral is not a verified repair; conversation is not automatic release approval.',
    target: 'finding-record',
    fallbackTarget: 'findings',
    destination: { section: 'engagement', tab: 'findings' },
  },
  {
    id: 'trusted-runs',
    chapter: 'tools',
    shortLabel: 'Run evidence',
    title: 'Check which version was actually exercised',
    description:
      'Run history records verification for an exact submitted revision. It starts empty, and trusted execution may be unavailable in some environments.',
    details: [
      'Compare the suite, status, revision and individual check results.',
      'Read actual browser receipts when present; a screenshot does not prove every interaction.',
      'Source review can still be useful when execution is unavailable, but it is different evidence.',
    ],
    tip: 'Start with the first failed check and its evidence before deciding whether the code or expectation needs attention.',
    target: 'runs',
    destination: { section: 'operations' },
  },
  {
    id: 'background-operations',
    chapter: 'tools',
    shortLabel: 'Progress and retry',
    title: 'Recover interrupted work without guessing what happened',
    description:
      'Background operations show work that is queued, running, completed or needs attention. Use the recorded status and explanation before deciding whether to retry.',
    details: [
      'A failed review can be retried through its available control, using saved progress when applicable.',
      'Cancelling stops future results; actions that already committed remain.',
      'An empty history simply means there are no operations to show yet.',
    ],
    tip: 'Read the failure explanation first, correct any stated prerequisite, then use Retry when available.',
    target: 'operations-history',
    fallbackTarget: 'runs',
    destination: { section: 'operations' },
  },
  {
    id: 'follow-up-tickets',
    chapter: 'tools',
    shortLabel: 'Tickets',
    title: 'Plan an increment with evidence of completion',
    description:
      'Tickets organize the work you decide to take on. Use their acceptance criteria and related decisions to connect implementation to agreed outcomes.',
    details: [
      'Create a follow-up ticket from a relevant finding when it needs implementation work.',
      'Inspect dependencies and acceptance criteria before changing a ticket’s status.',
      'Link a ticket when submitting the increment it describes.',
    ],
    tip: 'Prefer a small increment with clear acceptance over a ticket that tries to finish the whole product.',
    target: 'tickets',
    destination: { section: 'board' },
  },
  {
    id: 'shared-documents',
    chapter: 'tools',
    shortLabel: 'Documents',
    title: 'Keep deliveries and reference material traceable',
    description:
      'Documents separates your engineering deliveries from reference material. If the list is empty, you can later record a delivery or request a document from a colleague.',
    details: [
      'Check each document’s author, version, sources and delivery status.',
      'Share the specific version with the intended colleague when it is ready.',
      'A saved document and a delivered document are different states.',
    ],
    tip: 'Use a versioned handoff to record the delivered scope, remaining questions and supporting evidence.',
    target: 'documents',
    destination: { section: 'documents' },
  },
  {
    id: 'release-boundary',
    chapter: 'tools',
    shortLabel: 'Release',
    title: 'Separate readiness from a release decision',
    description:
      'Release concerns the simulated company cohort. Select a candidate and inspect its evidence when you are ready; a completed review alone does not release it.',
    details: [
      'Read the readiness status and any blockers for the selected revision.',
      'Ask Amara to inspect the current evidence and make the release decision.',
      'Confirm the recorded status becomes Released before treating the handoff as complete.',
    ],
    tip: 'If the candidate changes, check the new revision’s evidence before relying on earlier readiness.',
    target: 'release',
    fallbackTarget: 'runs',
    destination: { section: 'operations' },
  },
  {
    id: 'assessment-boundary',
    chapter: 'tools',
    shortLabel: 'Assessment',
    title: 'Use evidence to understand readiness, not to infer a grade',
    description:
      'Assessment brings together the evidence for the engagement. Before a check has been requested, it explains the next step instead of inventing a readiness result.',
    details: [
      'Read which requirements are satisfied and which still need evidence when results are available.',
      'Your discovery, delivery and handling of uncertainty all matter.',
      'Final readiness is moderated by an instructor; ordinary reviews do not automatically assign a grade.',
    ],
    tip: 'Follow a missing requirement back to its evidence before deciding what work to do next.',
    target: 'assessment',
    destination: { section: 'assessment' },
  },
  {
    id: 'find-your-way-back',
    chapter: 'tools',
    shortLabel: 'Find work',
    title: 'Return to the people and evidence you need',
    description:
      'Search Workbench finds work and people in your engagement. Brief, Build, Conversations and Reviews stay in the main navigation; other areas live under Project tools.',
    details: [
      'Search for a colleague, ticket, document or saved work item when you need it.',
      'On a compact layout, use the section picker; expand the navigation rail to use search.',
      'Return to any tour chapter later without resetting the work you have already done.',
    ],
    tip: 'Choose your own next step: clarify a question, build an increment or submit work for feedback.',
    target: 'field-search',
    fallbackTarget: 'brief-overview',
    destination: { section: 'engagement', tab: 'brief' },
  },
];
